import React, { useRef, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Play, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { HERO_AVATARS } from "@/lib/lumiqoData";

const SHOWREEL_URL =
  "https://res.cloudinary.com/owzt7ro1/video/upload/v1784210414/blaster_web_F_xl2meg.mp4";

/* Premium glass showreel card: 4:3 inline video + booking CTA */
const ShowreelCard = ({ className = "", testId = "hero-showreel", onBookCall }) => {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);

  const handlePlay = () => {
    setPlaying(true);
    requestAnimationFrame(() => {
      const p = videoRef.current?.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    });
  };

  return (
    <div
      className={`lq-glass lq-shadow-glow rounded-3xl p-3 ${className}`}
      style={{ background: "rgba(255,255,255,0.8)" }}
      data-testid={testId}
    >
      <div className="relative aspect-[4/3] rounded-2xl overflow-hidden bg-[#0a1d4d]">
        <video
          ref={videoRef}
          src={SHOWREEL_URL}
          controls={playing}
          muted
          playsInline
          preload="metadata"
          className="absolute inset-0 w-full h-full object-cover"
          data-testid={`${testId}-video`}
        />
        {!playing && (
          <button
            type="button"
            onClick={handlePlay}
            className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-gradient-to-br from-[#0a1d4d]/85 to-[#1d72ff]/60 group cursor-pointer"
            aria-label="Play showreel"
            data-testid={`${testId}-play`}
          >
            <span className="absolute inset-0 lq-noise opacity-30 pointer-events-none" />
            <span className="relative w-14 h-14 rounded-full bg-white/95 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
              <Play className="w-6 h-6 text-[#0a1d4d] fill-current ml-0.5" />
            </span>
            <span className="relative mt-3 text-white font-display font-bold text-sm">
              Watch Showreel
            </span>
          </button>
        )}
      </div>
      <button
        type="button"
        onClick={onBookCall}
        className="mt-3 flex w-full items-center justify-center gap-2 h-11 rounded-full bg-[#1d72ff] text-white text-sm font-semibold hover:bg-[#0a1d4d] hover:-translate-y-0.5 transition-[background-color,transform] duration-300"
        data-testid={`${testId}-book-call`}
      >
        📅 Book a Free Call
      </button>
      <p className="mt-2.5 mb-1 text-center text-xs text-[#0a1d4d]/60">
        Let’s discuss your project and see how I can help.
      </p>
    </div>
  );
};

/* ---- Official brand icons (accurate SVG logos) ---- */
const YouTubeIcon = () => (
  <svg viewBox="0 0 24 24" className="w-full h-full" aria-hidden="true">
    <path
      fill="#FF0000"
      d="M23.5 6.19a3.02 3.02 0 0 0-2.12-2.14C19.51 3.55 12 3.55 12 3.55s-7.51 0-9.38.5A3.02 3.02 0 0 0 .5 6.19C0 8.07 0 12 0 12s0 3.93.5 5.81a3.02 3.02 0 0 0 2.12 2.14c1.87.5 9.38.5 9.38.5s7.51 0 9.38-.5a3.02 3.02 0 0 0 2.12-2.14C24 15.93 24 12 24 12s0-3.93-.5-5.81z"
    />
    <path fill="#FFFFFF" d="M9.55 15.57V8.43L15.82 12l-6.27 3.57z" />
  </svg>
);

const InstagramIcon = ({ uid = "d" }) => {
  const gid = `lqIgGrad-${uid}`;
  return (
    <svg viewBox="0 0 24 24" className="w-full h-full" aria-hidden="true">
      <defs>
        <radialGradient id={gid} cx="30%" cy="107%" r="150%">
          <stop offset="0%" stopColor="#fdf497" />
          <stop offset="5%" stopColor="#fdf497" />
          <stop offset="45%" stopColor="#fd5949" />
          <stop offset="60%" stopColor="#d6249f" />
          <stop offset="90%" stopColor="#285AEB" />
        </radialGradient>
      </defs>
      <rect width="24" height="24" rx="5.4" fill={`url(#${gid})`} />
      <rect
        x="5"
        y="5"
        width="14"
        height="14"
        rx="4"
        fill="none"
        stroke="#fff"
        strokeWidth="1.7"
      />
      <circle cx="12" cy="12" r="3.35" fill="none" stroke="#fff" strokeWidth="1.7" />
      <circle cx="16.35" cy="7.65" r="1.12" fill="#fff" />
    </svg>
  );
};

const PremiereProIcon = () => (
  <svg viewBox="0 0 24 24" className="w-full h-full" aria-hidden="true">
    <rect width="24" height="24" rx="5" fill="#00005B" />
    <text
      x="12"
      y="16.4"
      textAnchor="middle"
      fontFamily="'Plus Jakarta Sans', Arial, sans-serif"
      fontWeight="800"
      fontSize="11.5"
      fill="#9999FF"
    >
      Pr
    </text>
  </svg>
);

const AfterEffectsIcon = () => (
  <svg viewBox="0 0 24 24" className="w-full h-full" aria-hidden="true">
    <rect width="24" height="24" rx="5" fill="#00005B" />
    <text
      x="12"
      y="16.4"
      textAnchor="middle"
      fontFamily="'Plus Jakarta Sans', Arial, sans-serif"
      fontWeight="800"
      fontSize="11.5"
      fill="#9999FF"
    >
      Ae
    </text>
  </svg>
);

const DaVinciResolveIcon = () => (
  <img
    src="/davinci-resolve-icon.png"
    alt="DaVinci Resolve"
    draggable={false}
    className="w-full h-full object-contain select-none"
  />
);

// Compact tool list for the mobile strip (same official icons, smaller)
// (removed per request: tool icons are hidden below 768px)

// Floating orbiting tool icons (Instagram / YouTube / After Effects / DaVinci Resolve / Premiere Pro)
const FloatingApp = ({ label, top, left, delay = 0, size = 64, children }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.6, y: 30 }}
    animate={{ opacity: 1, scale: 1, y: 0 }}
    transition={{ delay, duration: 0.7, ease: "easeOut" }}
    className="absolute lq-float"
    style={{ top, left }}
    data-testid={`hero-orbit-${label.toLowerCase().replace(/\s+/g, "-")}`}
  >
    <div
      className="lq-glass lq-shadow-soft rounded-2xl flex items-center justify-center"
      style={{
        width: size,
        height: size,
        background: "rgba(255,255,255,0.85)",
      }}
    >
      <div
        className="rounded-xl flex items-center justify-center"
        style={{
          width: size * 0.62,
          height: size * 0.62,
        }}
      >
        {children}
      </div>
    </div>
    <div className="mt-2 text-center text-[10px] sm:text-xs font-medium text-[#0a1d4d]/70">
      {label}
    </div>
  </motion.div>
);

const Hero = ({ onPrimary, onSecondary }) => {
  return (
    <section
      id="top"
      className="relative pt-32 sm:pt-40 pb-20 sm:pb-28 overflow-hidden"
      data-testid="hero-section"
    >
      {/* Background orbs */}
      <div
        className="lq-orb"
        style={{
          background: "#1d72ff",
          width: 520,
          height: 520,
          top: -180,
          left: -120,
          opacity: 0.25,
        }}
      />
      <div
        className="lq-orb"
        style={{
          background: "#0a1d4d",
          width: 460,
          height: 460,
          top: 60,
          right: -180,
          opacity: 0.18,
        }}
      />
      <div className="absolute inset-0 lq-grid-bg opacity-40 [mask-image:radial-gradient(circle_at_center,black,transparent_70%)]" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-[1.1fr_0.9fr] gap-10 lg:gap-16 items-center">
        {/* Left: copy */}
        <div className="relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white lq-shadow-soft border border-[#0a1d4d]/10 text-xs sm:text-sm font-medium text-[#0a1d4d]"
            data-testid="hero-badge"
          >
            <Sparkles className="w-3.5 h-3.5 text-[#1d72ff]" />
            Premium agency for short-form & social
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl xl:text-7xl text-[#0a1d4d] mt-6 leading-[1.05]"
            data-testid="hero-headline"
          >
            We Make Your Brand{" "}
            <span className="relative inline-block">
              <span className="lq-text-gradient">Go Viral</span>
              <svg
                className="absolute -bottom-2 left-0 w-full"
                viewBox="0 0 300 14"
                fill="none"
              >
                <path
                  d="M2 9 C 80 2, 200 16, 298 6"
                  stroke="#1d72ff"
                  strokeWidth="3"
                  strokeLinecap="round"
                  fill="none"
                />
              </svg>
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="mt-6 text-base sm:text-lg text-[#0a1d4d]/70 max-w-xl leading-relaxed"
            data-testid="hero-subheadline"
          >
            Premium video editing, scroll-stopping thumbnails and social media
            growth —
            built for brands that want{" "}
            <span className="text-[#0a1d4d] font-semibold">results</span>, not
            just content.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="mt-9 flex flex-col sm:flex-row gap-3"
          >
            <Button
              onClick={onPrimary}
              size="lg"
              className="h-12 rounded-full bg-[#1d72ff] hover:bg-[#0a1d4d] text-white font-semibold px-7 transition-colors group lq-shadow-glow"
              data-testid="hero-primary-cta"
            >
              See Our Work
              <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Button
              onClick={onSecondary}
              variant="outline"
              size="lg"
              className="h-12 rounded-full border-2 border-[#0a1d4d]/15 text-[#0a1d4d] hover:bg-[#0a1d4d] hover:text-white font-semibold px-7 bg-white transition-colors"
              data-testid="hero-secondary-cta"
            >
              <Play className="mr-2 w-4 h-4 fill-current" />
              Book a Free Call
            </Button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.5 }}
            className="mt-10 flex items-center gap-5"
          >
            <div className="flex -space-x-2">
              {HERO_AVATARS.map((a, i) => (
                <img
                  key={i}
                  src={a.src}
                  alt={a.alt}
                  loading="lazy"
                  className="w-9 h-9 rounded-full border-2 border-white object-cover bg-[#f5f6fa]"
                  data-testid={`hero-avatar-${i}`}
                />
              ))}
            </div>
            <div className="text-xs sm:text-sm">
              <div className="font-semibold text-[#0a1d4d]">
                Trusted by 50+ creators & brands
              </div>
              <div className="text-[#0a1d4d]/60">
                10M+ views generated · 500+ videos shipped
              </div>
            </div>
          </motion.div>

          {/* Mobile-only (<768px): showreel video card + Book a Call (no tool icons) */}
          <div className="md:hidden mt-10" data-testid="hero-mobile-showreel-block">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.55 }}
              className="max-w-[320px] mx-auto"
            >
              <ShowreelCard testId="hero-showreel-mobile" onBookCall={onSecondary} />
            </motion.div>
          </div>
        </div>

        {/* Right: orbit canvas (768px and up only) */}
        <div className="relative h-[480px] lg:h-[560px] hidden md:block">
          {/* Glow ring */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div
              className="rounded-full lq-spin-slow"
              style={{
                width: "82%",
                height: "82%",
                border: "1.5px dashed rgba(10,29,77,0.18)",
              }}
            />
          </div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div
              className="rounded-full"
              style={{
                width: "58%",
                height: "58%",
                border: "1.5px dashed rgba(29,114,255,0.25)",
              }}
            />
          </div>

          {/* Center showreel card */}
          <motion.div
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10 w-[300px] lg:w-[340px]"
            data-testid="hero-center-card"
          >
            <ShowreelCard testId="hero-showreel-desktop" onBookCall={onSecondary} />
          </motion.div>

          {/* Orbiting apps */}
          <FloatingApp label="Instagram" top="6%" left="55%" delay={0.4}>
            <InstagramIcon />
          </FloatingApp>
          <FloatingApp label="YouTube" top="22%" left="6%" delay={0.55}>
            <YouTubeIcon />
          </FloatingApp>
          <FloatingApp
            label="After Effects"
            top="60%"
            left="2%"
            delay={0.7}
            size={58}
          >
            <AfterEffectsIcon />
          </FloatingApp>
          <FloatingApp
            label="DaVinci Resolve"
            top="80%"
            left="42%"
            delay={0.85}
            size={62}
          >
            <DaVinciResolveIcon />
          </FloatingApp>
          <FloatingApp
            label="Premiere Pro"
            top="40%"
            left="78%"
            delay={1.0}
            size={66}
          >
            <PremiereProIcon />
          </FloatingApp>
        </div>
      </div>
    </section>
  );
};

export default Hero;

